import React, { useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import { Close as CloseIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { useMagicReport } from '../context/MagicReportContext';
import { BookmarkedSection, getBookmarkedSections, reorderSectionActionItems } from './action-items.util';
import {
  APPLICATION_COMMENTS_SECTION_CONFIG,
  CHANGE_ORDERS_SECTION_CONFIG,
  DOCUMENTS_RECEIVED_SECTION_CONFIG,
  FOUNDATION_SURVEY_SECTION_CONFIG,
  LIEN_WAIVERS_SECTION_CONFIG,
  MATERIAL_TESTING_REPORTS_SECTION_CONFIG,
  MINOR_ADJUSTMENTS_SECTION_CONFIG,
  NOTARY_SECTION_CONFIG,
  PLAN_COST_REVIEW_SECTION_CONFIG,
  PROGRESS_DESCRIPTION_SECTION_CONFIG,
  REALLOCATIONS_SECTION_CONFIG,
  REDUCTION_COMMENTS_SECTION_CONFIG,
  RETAINAGE_SECTION_CONFIG,
  SCHEDULE_SECTION_CONFIG,
  STORED_MATERIALS_SECTION_CONFIG,
} from './sections.config';

const DRAG_TYPE = 'ACTION_SECTION_ROW';

/** Maps section keys to display labels. Built from CompleteSectionConfig to avoid string duplication.
 *  Both the original id and its camelCase form are indexed so kebab-stored and camelCase-stored keys both resolve.
 *  Sections without a CompleteSectionConfig (e.g. previousFundings) are added manually. */
const SECTION_LABELS: Record<string, string> = {
  // Sections without a CompleteSectionConfig, or that may appear under either key form
  previousFundings: 'Previous Fundings',
  'previous-fundings': 'Previous Fundings',
  // All other sections keyed exactly as stored in sections state (cfg.id matches sectionId)
  ...Object.fromEntries(
    [
      APPLICATION_COMMENTS_SECTION_CONFIG,
      CHANGE_ORDERS_SECTION_CONFIG,
      DOCUMENTS_RECEIVED_SECTION_CONFIG,
      FOUNDATION_SURVEY_SECTION_CONFIG,
      LIEN_WAIVERS_SECTION_CONFIG,
      MATERIAL_TESTING_REPORTS_SECTION_CONFIG,
      MINOR_ADJUSTMENTS_SECTION_CONFIG,
      NOTARY_SECTION_CONFIG,
      PLAN_COST_REVIEW_SECTION_CONFIG,
      PROGRESS_DESCRIPTION_SECTION_CONFIG,
      REALLOCATIONS_SECTION_CONFIG,
      REDUCTION_COMMENTS_SECTION_CONFIG,
      RETAINAGE_SECTION_CONFIG,
      SCHEDULE_SECTION_CONFIG,
      STORED_MATERIALS_SECTION_CONFIG,
    ].map(cfg => [cfg.id, cfg.title]),
  ),
};

interface DragItem {
  index: number;
}

interface DraggableRowProps {
  section: BookmarkedSection;
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  onDrop: () => void;
}

const DraggableRow: React.FC<DraggableRowProps> = ({ section, index, moveRow, onDrop }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAG_TYPE,
    item: { index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    end: () => onDrop(),
  });

  const [, dropRef] = useDrop<DragItem>({
    accept: DRAG_TYPE,
    hover: (dragItem, monitor) => {
      if (!ref.current) return;
      const dragIndex = dragItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveRow(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    },
  });

  dragRef(dropRef(ref));

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        px: 1,
        borderRadius: 1,
        opacity: isDragging ? 0.35 : 1,
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
        border: '1px solid',
        borderColor: isDragging ? 'primary.light' : 'divider',
        mb: 1,
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        transition: 'border-color 0.15s, opacity 0.15s',
      }}
    >
      <Tooltip title='Drag to reorder' placement='left'>
        <DragIndicatorIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
      </Tooltip>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '0.7rem',
          fontWeight: 700,
        }}
      >
        {index + 1}
      </Box>
      <Typography variant='body2' sx={{ fontWeight: 500 }}>
        {section.label}
      </Typography>
    </Box>
  );
};

interface ActionItemsReorderModalProps {
  open: boolean;
  onClose: () => void;
}

export const ActionItemsReorderModal: React.FC<ActionItemsReorderModalProps> = ({ open, onClose }) => {
  const { reportState, setReportState, setIsDirty, resetActionItemOrderCounter, syncAllSectionOrders, flushAllPendingUpdates } =
    useMagicReport();
  const [localOrder, setLocalOrder] = useState<BookmarkedSection[] | null>(null);

  const sections = (reportState as any).sections;
  const derivedOrder = getBookmarkedSections(sections, SECTION_LABELS);

  const items = localOrder ?? derivedOrder;

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const next = [...(localOrder ?? derivedOrder)];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      setLocalOrder(next);
    },
    [localOrder, derivedOrder],
  );

  const commitOrder = useCallback(
    (order: BookmarkedSection[]) => {
      // Flush all pending section debounces first so no in-flight write can stomp the reorder.
      flushAllPendingUpdates();
      const currentSections = (reportState as any).sections;
      const updatedSections = reorderSectionActionItems(currentSections, order);
      setReportState((prev: any) => ({ ...prev, sections: updatedSections }));
      setIsDirty(true);
      resetActionItemOrderCounter();
      setLocalOrder(null);
      // Patch each section's localData so it doesn't stomp the new order on next flush
      syncAllSectionOrders(updatedSections);
    },
    [reportState, setReportState, setIsDirty, resetActionItemOrderCounter, syncAllSectionOrders, flushAllPendingUpdates],
  );

  const handleDrop = useCallback(() => {
    if (localOrder) commitOrder(localOrder);
  }, [localOrder, commitOrder]);

  const handleClose = () => {
    if (localOrder) commitOrder(localOrder);
    onClose();
  };

  return (
    <Dialog open={open && items.length > 0} onClose={handleClose} maxWidth='xs' fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2, pb: 1 }}>
        <Typography variant='h6'>Reorder Action Items</Typography>
        <IconButton size='small' onClick={handleClose} edge='end'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5, mx: 0.5 }}>
          Drag sections to set the order they appear in the Action Items list. Changes apply when you close.
        </Typography>
        <Box>
          {items.map((section, index) => (
            <DraggableRow key={section.sectionKey} section={section} index={index} moveRow={moveRow} onDrop={handleDrop} />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
